import { logger } from '@/utils/logger';
import {
  kakaoAuth,
  completeGoogleKakaoSignup,
  revokeGoogleAuth,
  revokeKakaoAuth,
} from '@/services/auth/socialAuthService';
import { deleteAccount } from '@/services/auth/userService';
import { useAuthStore } from '@/stores/authStore';
import { User } from '@/types/app_types';
import { cookieUtils } from '@/utils/cookie';
import { unlinkKakaoAccount } from '@/utils/kakaoAuth';

interface SocialAuthResult {
  success: boolean;
  requiresAgreement: boolean;
  user?: User;
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
  tempData?: {
    provider: 'google' | 'kakao';
    tempToken: string;
    email: string;
    name: string;
  };
  error?: string;
}

export const useSocialAuth = () => {
  const { setUser } = useAuthStore();

  // 구글/카카오 회원가입 완료 (통합)
  const completeSocialSignup = async (
    tempToken: string,
    provider: 'google' | 'kakao',
    fullName?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await completeGoogleKakaoSignup(tempToken, fullName);

      if (response.success && response.data) {
        const { user: userData, accessToken, refreshToken } = response.data;

        if (accessToken && refreshToken) {
          cookieUtils.set('accessToken', accessToken);
          cookieUtils.set('refreshToken', refreshToken);
        }

        const user: User = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          provider,
          walletAddress: null,
          ensName: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isActive: true,
          profile: {
            nftsOwned: 0,
            totalPurchases: 0,
          },
        };

        setUser(user);
        return { success: true };
      }

      return { success: false, error: response.message || '회원가입 완료 중 오류가 발생했습니다.' };
    } catch (error) {
      logger.error(`Complete ${provider} signup failed:`, error);
      return { success: false, error: '회원가입 완료 중 오류가 발생했습니다.' };
    }
  };

  // 카카오 로그인
  const kakaoLogin = async (kakaoAccessToken: string): Promise<SocialAuthResult> => {
    try {
      const response = await kakaoAuth(kakaoAccessToken);

      if (!response.success || !response.data) {
        return {
          success: false,
          requiresAgreement: false,
          error: response.message || '카카오 로그인 중 오류가 발생했습니다.',
        };
      }

      const { status, user, accessToken, refreshToken, tempToken } = response.data;

      // 가입된 사용자
      if (status === 'registered' && user && accessToken && refreshToken) {
        const userData: User = {
          id: user.id,
          email: user.email,
          name: user.name,
          provider: 'kakao',
          walletAddress: null,
          ensName: null,
          createdAt: user.createdAt || new Date().toISOString(),
          updatedAt: user.updatedAt || new Date().toISOString(),
          isActive: true,
          profile: {
            nftsOwned: 0,
            totalPurchases: 0,
          },
        };

        return {
          success: true,
          requiresAgreement: false,
          user: userData,
          tokens: { accessToken, refreshToken },
        };
      }

      // 미가입 사용자 - 약관 동의 필요
      if (status === 'not_registered' && tempToken) {
        return {
          success: true,
          requiresAgreement: true,
          tempData: {
            provider: 'kakao',
            tempToken,
            email: user?.email || '',
            name: user?.name || '',
          },
        };
      }

      return {
        success: false,
        requiresAgreement: false,
        error: '예상치 못한 응답 형식입니다.',
      };
    } catch (error) {
      logger.error('Kakao login failed:', { error: error });
      return {
        success: false,
        requiresAgreement: false,
        error: '카카오 로그인 중 오류가 발생했습니다.',
      };
    }
  };

  // 구글 계정 연결 해제 및 탈퇴
  const deleteGoogleAccount = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const user = useAuthStore.getState().user;
      if (!user) {
        return { success: false, error: '로그인이 필요합니다.' };
      }

      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }

      const revokeResult = await revokeGoogleAuth(user.id);
      if (!revokeResult.success) {
        return { success: false, error: revokeResult.message };
      }

      const deleteResult = await deleteAccount(true);
      if (deleteResult.success) {
        useAuthStore.getState().clearUser();

        if (window.google?.accounts?.id) {
          document.cookie = 'g_state=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }

        return { success: true };
      }

      return { success: false, error: deleteResult.message };
    } catch (error) {
      logger.error('Google account deletion error:', { error: error });
      return { success: false, error: '회원탈퇴 중 오류가 발생했습니다.' };
    }
  };

  // 카카오 계정 연결 해제 및 탈퇴
  const deleteKakaoAccount = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const user = useAuthStore.getState().user;
      if (!user) {
        return { success: false, error: '로그인이 필요합니다.' };
      }

      if (!window.Kakao?.Auth) {
        logger.error('Kakao SDK not loaded');
        return { success: false, error: '카카오 SDK가 로드되지 않았습니다.' };
      }

      const kakaoToken = window.Kakao.Auth.getAccessToken();

      if (kakaoToken) {
        const unlinkResult = await unlinkKakaoAccount();

        if (!unlinkResult.success) {
          logger.error('Kakao unlink failed:', unlinkResult.error);
        } else {
          logger.info('Kakao unlink successful');
        }

        if (window.Kakao.Auth.getAccessToken()) {
          await new Promise<void>(resolve => {
            window.Kakao.Auth.logout(() => {
              resolve();
            });
          });
        }
      }

      const revokeResult = await revokeKakaoAuth(user.id);
      if (!revokeResult.success) {
        logger.error('Backend revoke failed:', revokeResult.message);
      }

      const deleteResult = await deleteAccount(true);

      if (deleteResult.success) {
        useAuthStore.getState().clearUser();
        return { success: true };
      }

      return { success: false, error: deleteResult.message };
    } catch (error) {
      logger.error('Kakao account deletion error:', { error: error });
      return { success: false, error: '회원탈퇴 중 오류가 발생했습니다.' };
    }
  };

  return {
    completeSocialSignup,
    kakaoLogin,
    deleteGoogleAccount,
    deleteKakaoAccount,
  };
};